import React, { useState, useEffect } from "react";
import "../../css/GetStarted_New.css";
import { motion, AnimatePresence } from "framer-motion";
import { FiDownload, FiUpload, FiCheckCircle, FiArrowRight, FiPlay } from "react-icons/fi";
import { FaApple } from "react-icons/fa";
import mobileal from "../../images/mobileal.png";
import mobiles2 from "../../images/mobiles2.JPG";

export const GetStarted_New = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [progress, setProgress] = useState(0);

  // Auto-advancing logic with progress bar
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveStep((step) => (step === 3 ? 1 : step + 1));
          return 0;
        }
        return prev + 1; // 100 increments over ~4 seconds = 40ms interval
      });
    }, 40);

    return () => clearInterval(timer);
  }, [activeStep]);

  const handleStepClick = (stepId) => {
    setActiveStep(stepId);
    setProgress(0);
  };

  const steps = [
    {
      id: 1,
      title: "Fast Download",
      headline: "Get the App.",
      desc: "Experience the fastest loan approval process right from your pocket. Download our highly secure application in seconds.",
      icon: <FiDownload />,
      image: mobileal,
      color: "#3b82f6" // Blue
    },
    {
      id: 2,
      title: "Smart Upload",
      headline: "Verify Identity.",
      desc: "Our AI-powered KYC engine uses advanced facial recognition to verify your documents securely in under 10 minutes.",
      icon: <FiUpload />,
      image: mobiles2,
      color: "#8b5cf6" // Purple
    },
    {
      id: 3,
      title: "10 Min Credit",
      headline: "Get Funded.",
      desc: "Once approved, your capital is instantly disbursed directly to your registered bank account without any delays.",
      icon: <FiCheckCircle />,
      image: mobileal,
      color: "#10b981" // Emerald
    }
  ];

  const activeData = steps.find(s => s.id === activeStep);

  return (
    <section className="gs_v15_wrapper">
      
      {/* Dynamic Background that changes color slightly based on active step */}
      <motion.div 
        className="gs_v15_dynamic_bg"
        animate={{ backgroundColor: `${activeData.color}08` }} // 08 hex for 5% opacity
        transition={{ duration: 1 }}
      ></motion.div>

      <div className="gs_v15_container">
        
        {/* Main Presentation Stage */}
        <div className="gs_v15_stage">
          
          {/* Left Text Content */}
          <div className="gs_v15_text_content">
            <span className="gs_v15_badge">DIGITAL ONBOARDING</span>
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeStep}
                initial={{ opacity: 0, y: 20, filter: "blur(5px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -20, filter: "blur(5px)" }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <h2 className="gs_v15_headline">
                  {activeData.headline.split('.')[0]}<span style={{ color: activeData.color }}>.</span>
                </h2>
                <p className="gs_v15_desc">{activeData.desc}</p>
                
                <div className="gs_v15_step_number" style={{ color: activeData.color }}>
                  0{activeData.id}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="gs_v15_buttons">
                <a href="#" className="v15_btn dark"><FaApple /> App Store</a>
                <a href="https://play.google.com/store/apps/details?id=com.actoloan.app.actoloan" target="_blank" rel="noopener noreferrer" className="v15_btn outline"><FiPlay /> Google Play</a>
            </div>
          </div>

          {/* Right Phone Presentation */}
          <div className="gs_v15_phone_presentation">
            
            {/* Glowing Aura behind phone */}
            <motion.div 
              className="gs_v15_phone_aura"
              animate={{ background: `radial-gradient(circle, ${activeData.color}40 0%, transparent 70%)` }}
              transition={{ duration: 1 }}
            ></motion.div>

            <div className="gs_v15_phone_mockup">
              <AnimatePresence mode="wait">
                <motion.img 
                  key={activeStep}
                  src={activeData.image}
                  alt="App Interface"
                  loading="lazy"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                />
              </AnimatePresence>
            </div>
          </div>

        </div>

        {/* The "Dynamic Dock" Navigation */}
        <div className="gs_v15_dock_wrapper">
          <div className="gs_v15_dock">
            {steps.map((step) => {
              const isActive = activeStep === step.id;
              return (
                <div 
                  key={step.id}
                  className={`gs_v15_dock_item ${isActive ? 'active' : ''}`}
                  onClick={() => handleStepClick(step.id)}
                >
                  <div className="dock_item_content">
                    <span className="dock_icon" style={{ color: isActive ? step.color : "#94a3b8" }}>
                      {step.icon}
                    </span>
                    <span className="dock_title">{step.title}</span>
                  </div>
                  
                  {/* Progress Bar background track */}
                  <div className="dock_progress_track">
                    {/* Active Progress Fill */}
                    {isActive && (
                      <motion.div 
                        className="dock_progress_fill"
                        style={{ backgroundColor: step.color }}
                        initial={{ width: "0%" }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0, ease: "linear" }}
                      ></motion.div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};
